import { Link, useParams } from "react-router-dom";
import {
  Download,
  ShareNetwork,
  Copy,
  Envelope,
  Phone,
  SealCheck,
  FilePdf,
  File,
  FileZip,
  Images,
  ArrowLeft,
  Printer,
  Eye,
  TrendUp,
  Quotes,
} from "@phosphor-icons/react";
import {
  getArtist,
  getPressKit,
  getCredentials,
} from "@/data/mockData";

const downloadIcon = (label) => {
  if (label.includes("ZIP")) return FileZip;
  if (label.includes("PDF")) return FilePdf;
  if (label.includes("resolución") || label.includes("Fotos")) return Images;
  return File;
};

export default function PressKit() {
  const { slug } = useParams();
  const artist = getArtist(slug);
  const kit = getPressKit(slug);
  const credentials = getCredentials(slug);
  const shareUrl = `elarte.net/prensa/${slug}`;

  return (
    <div
      data-testid="press-kit-page"
      className="min-h-screen bg-[#050505] text-[#F4F4F5]"
    >
      {/* Minimal top bar — press kit is a public standalone page */}
      <header className="sticky top-0 z-30 bg-[#050505]/90 backdrop-blur-lg border-b border-[#27272A]">
        <div className="max-w-[1300px] mx-auto flex items-center justify-between gap-4 px-4 md:px-8 h-16">
          <Link
            to={`/artista/${artist.slug}`}
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-white inline-flex items-center gap-2"
          >
            <ArrowLeft size={12} /> Perfil de {artist.name.split(" ")[0]}
          </Link>
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] hidden md:block">
            KIT DE PRENSA · ELARTE/NET
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="copy-link"
              className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] border border-[#27272A] hover:border-[#FF3E00] hover:text-[#FF3E00] px-3 py-2 inline-flex items-center gap-2"
            >
              <Copy size={12} /> <span className="hidden sm:inline">Copiar link</span>
            </button>
            <button
              data-testid="download-all"
              className="btn-brutal font-mono uppercase text-[10px] tracking-[0.22em] bg-[#FF3E00] text-white px-3 py-2 font-bold inline-flex items-center gap-2"
            >
              <Download size={12} weight="bold" /> Descargar kit
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-[#27272A]">
          <div className="grid grid-cols-1 md:grid-cols-[420px_1fr]">
            {/* Portrait */}
            <div className="relative aspect-[4/5] bg-[#121212] overflow-hidden">
              <img
                src={artist.avatar}
                alt={artist.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.22em] bg-black/60 backdrop-blur-sm border border-white/20 text-white px-2 py-1">
                FOTO PRENSA · ALTA RES
              </div>
              <div className="absolute bottom-3 right-3">
                <button className="h-10 w-10 bg-[#FF3E00] text-white inline-flex items-center justify-center">
                  <Download size={16} weight="bold" />
                </button>
              </div>
            </div>
            {/* Identity */}
            <div className="p-5 md:p-10 flex flex-col justify-between">
              <div>
                <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
                  {artist.discipline} · {artist.city.toUpperCase()}
                </div>
                <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter uppercase leading-[0.88]">
                  {artist.name}
                  {artist.verified ? (
                    <SealCheck
                      size={40}
                      weight="fill"
                      className="inline-block ml-3 text-[#FF3E00] align-text-top"
                    />
                  ) : null}
                </h1>
                <p className="font-display italic text-lg md:text-2xl text-[#A1A1AA] mt-5 max-w-xl leading-[1.35]">
                  {kit.shortBio}
                </p>
              </div>

              {/* Share block */}
              <div className="mt-8 border border-[#27272A] bg-[#0a0a0a] flex items-stretch">
                <div className="flex-1 px-4 py-3 min-w-0">
                  <div className="font-mono text-[9px] text-[#A1A1AA] tracking-[0.22em] mb-1">
                    LINK PÚBLICO · PARA COMPARTIR
                  </div>
                  <div className="font-mono text-sm text-[#FF3E00] truncate">
                    {shareUrl}
                  </div>
                </div>
                <button className="px-4 border-l border-[#27272A] hover:bg-[#121212] inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] hover:text-white">
                  <ShareNetwork size={14} />
                  <span className="hidden md:inline">Compartir</span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Quick audience stats strip */}
        <section className="grid grid-cols-2 md:grid-cols-4 border-b border-[#27272A]">
          <Stat
            label="SEGUIDORES"
            value={(kit.audience.followers / 1000).toFixed(1) + "K"}
            color="#FF3E00"
          />
          <Stat
            label="ALCANCE MENSUAL"
            value={(kit.audience.monthlyReach / 1000).toFixed(0) + "K"}
            color="#00FF66"
            divider
          />
          <Stat
            label="ENGAGEMENT"
            value={`${kit.audience.avgEngagement}%`}
            color="#FFD600"
            divider
          />
          <Stat
            label="PERFIL VERIFICADO"
            value={artist.verified ? "SÍ" : "NO"}
            divider
          />
        </section>

        {/* Bio + representation + downloads */}
        <section className="grid grid-cols-1 lg:grid-cols-[1fr_360px]">
          <div className="p-5 md:p-10 border-b lg:border-b-0 lg:border-r border-[#27272A]">
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-3">
              BIO LARGA
            </div>
            <p className="font-display text-lg md:text-2xl leading-[1.5] text-[#F4F4F5] max-w-3xl">
              {kit.longBio}
            </p>

            {/* Audience demographics */}
            <div className="mt-10">
              <div className="flex items-center gap-2 mb-4">
                <TrendUp size={14} weight="fill" className="text-[#FF3E00]" />
                <h3 className="font-display font-black uppercase tracking-tight text-lg">
                  Audiencia · por ciudad
                </h3>
              </div>
              <ul className="border border-[#27272A] max-w-md">
                {kit.audience.demographics.map((d) => (
                  <li
                    key={d.label}
                    className="grid grid-cols-[100px_1fr_60px] items-center gap-3 px-4 py-3 border-b border-[#27272A] last:border-b-0"
                  >
                    <span className="font-display font-semibold text-sm">
                      {d.label}
                    </span>
                    <div className="h-1 bg-[#27272A]">
                      <div
                        className="h-full bg-[#FF3E00]"
                        style={{ width: `${d.pct}%` }}
                      />
                    </div>
                    <span className="font-mono text-xs text-[#FF3E00] tracking-wider text-right">
                      {d.pct}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Downloads + Rep */}
          <aside className="p-5 md:p-6 space-y-6">
            <div className="border border-[#FF3E00] bg-[#FF3E00]/5">
              <header className="p-4 border-b border-[#FF3E00]/40 flex items-center gap-2">
                <Download size={14} weight="fill" className="text-[#FF3E00]" />
                <h3 className="font-display font-black uppercase tracking-tight">
                  Descargas
                </h3>
              </header>
              <ul>
                {kit.downloads.map((d, i) => {
                  const Ic = downloadIcon(d.label);
                  return (
                    <li
                      key={i}
                      className="flex items-center gap-3 px-4 py-3 border-b border-[#FF3E00]/30 last:border-b-0 hover:bg-[#FF3E00]/10 cursor-pointer group"
                    >
                      <Ic size={18} className="text-[#FF3E00]" weight="fill" />
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-sm truncate">
                          {d.label}
                        </div>
                        <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                          {d.size}
                        </div>
                      </div>
                      <Download
                        size={14}
                        className="text-[#A1A1AA] group-hover:text-[#FF3E00]"
                      />
                    </li>
                  );
                })}
              </ul>
              <div className="p-3 border-t border-[#FF3E00]/40 flex items-center justify-between">
                <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
                  TODO EL PAQUETE
                </span>
                <button className="font-mono uppercase text-[10px] tracking-[0.22em] bg-[#FF3E00] text-white px-3 py-1.5 font-bold">
                  Descargar zip
                </button>
              </div>
            </div>

            {/* Representation */}
            <div className="border border-[#27272A]">
              <header className="p-4 border-b border-[#27272A]">
                <h3 className="font-display font-black uppercase tracking-tight">
                  Representación & contacto
                </h3>
              </header>
              <div className="p-4 space-y-3">
                <div>
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-1">
                    REPRESENTACIÓN
                  </div>
                  <div className="font-display font-bold">
                    {kit.representation.gallery}
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-1">
                    CONTACTO DIRECTO
                  </div>
                  <a
                    href={`mailto:${kit.representation.contact.split(" · ")[1]}`}
                    className="font-display font-semibold text-sm text-[#FF3E00] hover:underline inline-flex items-center gap-2"
                  >
                    <Envelope size={12} />
                    {kit.representation.contact}
                  </a>
                </div>
                <div>
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-[0.22em] mb-1">
                    TELÉFONO
                  </div>
                  <span className="font-mono text-sm text-[#F4F4F5] inline-flex items-center gap-2">
                    <Phone size={12} />
                    {kit.representation.phone}
                  </span>
                </div>
              </div>
            </div>

            {/* Print */}
            <button
              data-testid="print-one-pager"
              className="btn-brutal w-full border border-[#F4F4F5] text-[#F4F4F5] hover:bg-[#F4F4F5] hover:text-black font-mono uppercase text-[10px] tracking-[0.22em] py-3 inline-flex items-center justify-center gap-2"
              onClick={() => window.print?.()}
            >
              <Printer size={13} /> Imprimir one-pager
            </button>
          </aside>
        </section>

        {/* Featured works */}
        <section className="p-5 md:p-8 border-t border-[#27272A]">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Eye size={16} weight="fill" className="text-[#FF3E00]" />
              <h2 className="font-display font-black uppercase tracking-tight text-xl">
                Obras destacadas · {kit.featuredWorks.length}
              </h2>
            </div>
            <span className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
              ALTA RESOLUCIÓN · LIBRE USO EDITORIAL
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {kit.featuredWorks.map((w, i) => (
              <figure
                key={w.id}
                data-testid={`press-work-${w.id}`}
                className="group relative overflow-hidden border border-[#27272A] bg-[#0a0a0a]"
              >
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={w.image}
                    alt={w.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.22em] bg-black/60 backdrop-blur-sm border border-white/20 text-white px-2 py-1">
                    {String(i + 1).padStart(2, "0")} / {String(kit.featuredWorks.length).padStart(2, "0")}
                  </div>
                  <button className="absolute bottom-3 right-3 h-10 w-10 bg-[#FF3E00] text-white inline-flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all">
                    <Download size={14} weight="bold" />
                  </button>
                </div>
                <figcaption className="p-3 border-t border-[#27272A]">
                  <div className="font-display font-bold text-sm truncate">
                    {w.title}
                  </div>
                  <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mt-1 truncate">
                    {w.medium} · {w.size} · {w.year}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* Press quotes */}
        {kit.press?.length > 0 ? (
          <section className="p-5 md:p-8 border-t border-[#27272A]">
            <div className="flex items-center gap-2 mb-5">
              <Quotes size={16} weight="fill" className="text-[#FF3E00]" />
              <h2 className="font-display font-black uppercase tracking-tight text-xl">
                Han dicho sobre {artist.name.split(" ")[0]}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {kit.press.map((p, i) => (
                <blockquote
                  key={i}
                  className="border-t-2 border-[#FF3E00] bg-[#0a0a0a] p-5 flex flex-col"
                >
                  <p className="font-display italic text-lg leading-[1.35] flex-1">
                    “{p.quote}”
                  </p>
                  <footer className="mt-4 pt-4 border-t border-[#27272A]">
                    <div className="font-display font-bold text-sm">
                      — {p.source}
                    </div>
                    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mt-1">
                      {p.date}
                    </div>
                  </footer>
                </blockquote>
              ))}
            </div>
          </section>
        ) : null}

        {/* CV / Credentials */}
        {credentials.length > 0 ? (
          <section className="p-5 md:p-8 border-t border-[#27272A]">
            <div className="flex items-center gap-2 mb-5">
              <SealCheck size={16} weight="fill" className="text-[#00FF66]" />
              <h2 className="font-display font-black uppercase tracking-tight text-xl">
                Trayectoria · verificada
              </h2>
            </div>
            <ul className="border border-[#27272A] max-w-3xl">
              {credentials.map((c, i) => (
                <li
                  key={i}
                  className="grid grid-cols-[100px_1fr_auto] gap-4 items-center px-4 py-3 border-b border-[#27272A] last:border-b-0"
                >
                  <span className="font-mono text-[12px] text-[#FF3E00] tracking-wider font-bold">
                    {c.year}
                  </span>
                  <span className="font-display font-semibold">{c.label}</span>
                  <SealCheck size={14} weight="fill" className="text-[#00FF66]" />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Foot */}
        <footer className="p-5 md:p-8 border-t border-[#27272A] flex items-center justify-between gap-3 flex-wrap">
          <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider">
            KIT DE PRENSA GENERADO AUTOMÁTICAMENTE DESDE ELARTE/NET · ACTUALIZADO EN VIVO
          </div>
          <Link
            to="/"
            className="font-display text-sm font-black uppercase hover:text-[#FF3E00]"
          >
            ELARTE<span className="text-[#FF3E00]">/</span>NET →
          </Link>
        </footer>
      </main>
    </div>
  );
}

function Stat({ label, value, color, divider }) {
  return (
    <div
      className={`p-4 md:p-5 text-center ${
        divider ? "border-l border-t md:border-t-0 border-[#27272A]" : ""
      }`}
    >
      <div className="font-mono text-[9px] text-[#A1A1AA] tracking-[0.22em] mb-2">
        {label}
      </div>
      <div
        className="font-display font-black text-2xl md:text-3xl tracking-tight"
        style={color ? { color } : {}}
      >
        {value}
      </div>
    </div>
  );
}
